'use client'

import { PersonAvatarFromPerson } from '@/components/person-avatar-from-person'
import {
  MessageCircle,
  ArrowRight,
  Clock,
  CheckCircle,
  Archive,
  Link as LinkIcon,
  GitPullRequest,
  Send,
} from '@/components/icons'
import { MarkdownContent } from '@/components/markdown-content'
import { preserveSoftBreaks } from '@/components/markdown/preserve-soft-breaks'
import { formatDate } from '@/lib/date-utils'
import { getDisplayName } from '@/lib/display-name'
import type { TimelineItem } from '@/app/actions/feedback-user'
import type {
  FeedbackStatus,
  FeedbackActivityType,
} from '@prisma/client-cadence'
import { SYSTEM_ACTOR_DISPLAY_NAME } from '@/lib/organization-constants'
import { isAdminRole } from '@/lib/role-utils'

interface FeedbackTimelineProps {
  timeline: TimelineItem[]
  feedbackCreatedAt: Date
  timezone: string
}

export function FeedbackTimeline({
  timeline,
  feedbackCreatedAt,
  timezone,
}: FeedbackTimelineProps) {
  const hasTimelineItems = timeline.length > 0

  return (
    <div className="relative">
      {/* Vertical line */}
      <div className="absolute left-3 top-3 bottom-3 w-px bg-fill-10" />

      {/* Submission event (always first) */}
      <TimelineEvent
        icon={<Send className="w-3.5 h-3.5" />}
        iconBg="bg-fill-10"
        iconColor="text-text-secondary"
        title="Feedback submitted"
        timestamp={feedbackCreatedAt}
        timezone={timezone}
        isFirst={true}
      />

      {/* Timeline items */}
      {timeline.map((item, index) => (
        <div key={item.id}>
          {item.type === 'comment' ? (
            <CommentEvent
              item={item}
              timezone={timezone}
              isLast={!hasTimelineItems || index === timeline.length - 1}
            />
          ) : (
            <ActivityEvent
              item={item}
              timezone={timezone}
              isLast={index === timeline.length - 1}
            />
          )}
        </div>
      ))}

      {/* Empty state */}
      {!hasTimelineItems && (
        <div className="ml-8 mt-4 text-sm text-text-secondary">
          No updates yet. Check back later for status changes and comments.
        </div>
      )}
    </div>
  )
}

interface TimelineEventProps {
  icon: React.ReactNode
  iconBg: string
  iconColor: string
  title: React.ReactNode
  subtitle?: string
  timestamp: Date
  timezone: string
  isFirst?: boolean
  isLast?: boolean
  children?: React.ReactNode
}

function TimelineEvent({
  icon,
  iconBg,
  iconColor,
  title,
  subtitle,
  timestamp,
  timezone,
  children,
}: TimelineEventProps) {
  return (
    <div className="relative flex gap-3 pb-4">
      {/* Icon */}
      <div
        className={`relative z-10 flex-shrink-0 w-6 h-6 rounded-full ${iconBg} ${iconColor} flex items-center justify-center`}
      >
        {icon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-medium text-text-primary">{title}</span>
          {subtitle && (
            <span className="text-sm text-text-secondary">{subtitle}</span>
          )}
          <span className="text-xs text-text-secondary">
            {formatDate(timestamp, timezone)}
          </span>
        </div>
        {children}
      </div>
    </div>
  )
}

interface CommentEventProps {
  item: Extract<TimelineItem, { type: 'comment' }>
  timezone: string
  isLast: boolean
}

function CommentEvent({ item, timezone }: CommentEventProps) {
  const isAdmin = isAdminRole(item.author.role)

  return (
    <div className="relative flex gap-3 pb-4">
      {/* Avatar as icon */}
      <div className="relative z-10 flex-shrink-0">
        <PersonAvatarFromPerson person={item.author} size="sm" disableHover />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
          <span className="text-sm font-medium text-text-primary">
            {/* #12268: both fields are null for an author this org cannot see,
                so `name || email` rendered an empty span next to an avatar that
                already said "Unknown" (PersonAvatarFromPerson routes through
                getDisplayName). Use the same helper here. */}
            {getDisplayName(item.author)}
          </span>
          {isAdmin && (
            <span className="px-1.5 py-0.5 text-xs font-medium bg-status-purple-bg text-status-purple-text-strong rounded">
              Admin
            </span>
          )}
          <span className="text-xs text-text-secondary">
            {formatDate(item.createdAt, timezone)}
          </span>
        </div>
        <div className="p-3 rounded-lg bg-surface-panel border border-stroke-regular">
          {/* #13707: comments are authored prose too. `compact` keeps the
              paragraph/list spacing tight enough for a comment bubble. */}
          <MarkdownContent
            compact
            content={preserveSoftBreaks(item.message)}
            className="text-sm text-text-primary"
          />
        </div>
      </div>
    </div>
  )
}

interface ActivityEventProps {
  item: Extract<TimelineItem, { type: 'activity' }>
  timezone: string
  isLast: boolean
}

function ActivityEvent({ item, timezone }: ActivityEventProps) {
  const { activityType, fromStatus, toStatus, metadata, actor, createdAt } =
    item

  // Get icon and colors based on activity type
  const { icon, iconBg, iconColor, title } = getActivityStyles(
    activityType,
    fromStatus,
    toStatus,
    metadata,
  )

  // Actor is null for system/automation actions (Issue #1422) — and ONLY for
  // those. #13755: a cross-org actor used to reach this branch too, because
  // the action passed the RLS-nulled relation straight through; it now arrives
  // as a `DisplayPerson` carrying CROSS_ORG_PERSON_LABEL, so `null` means what
  // it says again. `getDisplayName()` rather than `name || email` because both
  // of those are nullable and the result is interpolated (`by null`).
  const subtitle = actor
    ? `by ${getDisplayName(actor)}`
    : `by ${SYSTEM_ACTOR_DISPLAY_NAME}`

  return (
    <TimelineEvent
      icon={icon}
      iconBg={iconBg}
      iconColor={iconColor}
      title={title}
      subtitle={subtitle}
      timestamp={createdAt}
      timezone={timezone}
    />
  )
}

function getActivityStyles(
  activityType: FeedbackActivityType,
  fromStatus: FeedbackStatus | null,
  toStatus: FeedbackStatus | null,
  metadata: unknown,
): {
  icon: React.ReactNode
  iconBg: string
  iconColor: string
  title: React.ReactNode
} {
  switch (activityType) {
    case 'STATUS_CHANGE':
      return getStatusChangeStyles(fromStatus, toStatus)
    case 'LINKED_ISSUE': {
      const issueUrl = getMetadataUrl(metadata)
      const issueNumber = issueUrl ? extractNumberFromUrl(issueUrl) : null
      return {
        icon: <LinkIcon className="w-3.5 h-3.5" />,
        iconBg: 'bg-status-blue-bg',
        iconColor: 'text-status-blue-text',
        title: issueUrl ? (
          <>
            Linked to issue{' '}
            <a
              href={issueUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-status-blue-text hover:underline"
            >
              #{issueNumber}
            </a>
          </>
        ) : (
          'Linked to issue'
        ),
      }
    }
    case 'LINKED_PR': {
      const prUrl = getMetadataUrl(metadata)
      const prNumber = prUrl ? extractNumberFromUrl(prUrl) : null
      return {
        icon: <GitPullRequest className="w-3.5 h-3.5" />,
        iconBg: 'bg-status-purple-bg',
        iconColor: 'text-status-purple-text',
        title: prUrl ? (
          <>
            Linked to PR{' '}
            <a
              href={prUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-status-purple-text hover:underline"
            >
              #{prNumber}
            </a>
          </>
        ) : (
          'Linked to PR'
        ),
      }
    }
    default:
      return {
        icon: <MessageCircle className="w-3.5 h-3.5" />,
        iconBg: 'bg-fill-10',
        iconColor: 'text-text-secondary',
        title: 'Activity',
      }
  }
}

function getStatusChangeStyles(
  fromStatus: FeedbackStatus | null,
  toStatus: FeedbackStatus | null,
): {
  icon: React.ReactNode
  iconBg: string
  iconColor: string
  title: React.ReactNode
} {
  // Determine style based on target status
  switch (toStatus) {
    case 'NEW':
      return {
        icon: <Clock className="w-3.5 h-3.5" />,
        iconBg: 'bg-status-amber-bg',
        iconColor: 'text-status-amber-text',
        title: `Status: ${formatStatus(fromStatus)} ${String.fromCharCode(8594)} New`,
      }
    case 'ISSUE_CREATED':
      return {
        icon: <LinkIcon className="w-3.5 h-3.5" />,
        iconBg: 'bg-status-blue-bg',
        iconColor: 'text-status-blue-text',
        title: `Status: ${formatStatus(fromStatus)} ${String.fromCharCode(8594)} Issue Created`,
      }
    case 'IMPLEMENTING':
      return {
        icon: <Clock className="w-3.5 h-3.5" />,
        iconBg: 'bg-status-yellow-bg',
        iconColor: 'text-status-yellow-text',
        title: `Status: ${formatStatus(fromStatus)} ${String.fromCharCode(8594)} Implementing`,
      }
    case 'REVIEWED':
      return {
        icon: <CheckCircle className="w-3.5 h-3.5" />,
        iconBg: 'bg-status-blue-bg',
        iconColor: 'text-status-blue-text',
        title: `Status: ${formatStatus(fromStatus)} ${String.fromCharCode(8594)} Reviewed`,
      }
    case 'RESOLVED':
      return {
        icon: <CheckCircle className="w-3.5 h-3.5" />,
        iconBg: 'bg-status-green-bg',
        iconColor: 'text-status-green-text',
        title: `Status: ${formatStatus(fromStatus)} ${String.fromCharCode(8594)} Resolved`,
      }
    case 'CLOSED':
      return {
        icon: <Archive className="w-3.5 h-3.5" />,
        iconBg: 'bg-fill-10',
        iconColor: 'text-text-secondary',
        title: `Status: ${formatStatus(fromStatus)} ${String.fromCharCode(8594)} Closed`,
      }
    default:
      return {
        icon: <ArrowRight className="w-3.5 h-3.5" />,
        iconBg: 'bg-fill-10',
        iconColor: 'text-text-secondary',
        title: 'Status changed',
      }
  }
}

function formatStatus(status: FeedbackStatus | null): string {
  if (!status) return 'Unknown'
  return status.charAt(0) + status.slice(1).toLowerCase()
}

/**
 * Extract the issue/PR URL from metadata.
 * Checks both issueUrl (automation format) and url (legacy manual linking format)
 * for backwards compatibility (Issue #1872).
 */
function getMetadataUrl(metadata: unknown): string | null {
  if (metadata && typeof metadata === 'object') {
    // Check issueUrl first (new standard from automation)
    if ('issueUrl' in metadata && typeof metadata.issueUrl === 'string') {
      return metadata.issueUrl
    }
    // Fall back to url (legacy manual linking format)
    if ('url' in metadata && typeof metadata.url === 'string') {
      return metadata.url
    }
  }
  return null
}

function extractNumberFromUrl(url: string): string {
  // Extract issue/PR number from GitHub URL
  const match = url.match(/\/(?:issues|pull)\/(\d+)/)
  return match ? match[1] : url
}
